import { reactive } from 'vue'
import type { Router } from 'vue-router'

export function createRouteLoadingState() {
  return reactive({
    phase: 'loading' as 'loading' | 'ready' | 'error',
    pendingId: null as number | null,
    resolvedId: null as number | null,
    resolvedPath: null as string | null,
    renderedPath: null as string | null,
  })
}

export type RouteLoadingState = ReturnType<typeof createRouteLoadingState>

// Created before the router's first navigation so replacing the document shell
// with Vue never leaves the route outlet empty and unannounced.
export const routeLoading = createRouteLoadingState()

export function markRouteRendered(state: RouteLoadingState, path: string) {
  if (
    state.renderedPath !== null ||
    state.phase !== 'loading' ||
    state.pendingId === null ||
    state.pendingId !== state.resolvedId ||
    state.resolvedPath !== path
  ) return
  state.renderedPath = path
  state.pendingId = null
  state.phase = 'ready'
}

export function installRouteLoading(router: Router, state: RouteLoadingState = routeLoading) {
  let nextId = 0
  const navigationIds = new WeakMap<object, number>()

  router.beforeEach((to) => {
    const id = ++nextId
    navigationIds.set(to, id)
    state.pendingId = id
    state.resolvedId = null
    state.resolvedPath = null
    // Only the first route needs a loading shell. Once a route has rendered,
    // keep the existing view visible while later navigations resolve.
    state.phase = state.renderedPath ? 'ready' : 'loading'
  })

  router.afterEach((to, _from, failure) => {
    const id = navigationIds.get(to)
    if (id === undefined || id !== state.pendingId) return
    if (failure) {
      state.pendingId = null
      state.phase = state.renderedPath ? 'ready' : 'error'
      return
    }
    if (state.renderedPath) {
      state.pendingId = null
      state.phase = 'ready'
      return
    }
    state.resolvedId = id
    state.resolvedPath = to.fullPath
    // The route has resolved, but RouterView has not painted it yet.
  })

  router.onError((_error, to) => {
    const id = navigationIds.get(to)
    if (id === undefined || id !== state.pendingId) return
    state.pendingId = null
    state.resolvedId = null
    state.resolvedPath = null
    state.phase = 'error'
  })

  return {
    state,
    markRendered: (path: string) => markRouteRendered(state, path),
  }
}
